import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Briefcase, Wrench, FileText, HardHat, ShieldCheck, Clock, Users } from "lucide-react";
import PageShell from "@/components/PageShell";
import { api } from "@/lib/api";

const HERO_BG = "https://images.unsplash.com/photo-1664382953403-fc1ac77073a0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwd2FyZWhvdXNlJTIwZmFjdG9yeSUyMHdvcmtlcnN8ZW58MHx8fHwxNzc5MTI5OTk3fDA&ixlib=rb-4.1.0&q=85";

const SERVICES = [
    {
        title: "Industrial",
        Icon: Wrench,
        text: "Forklift, welders, assembly, machine operators, warehouse — the backbone of Fort Wayne manufacturing.",
        img: "https://images.unsplash.com/photo-1589792923962-537704632910?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHw0fHxpbmR1c3RyaWFsJTIwd2FyZWhvdXNlJTIwZmFjdG9yeSUyMHdvcmtlcnN8ZW58MHx8fHwxNzc5MTI5OTk3fDA&ixlib=rb-4.1.0&q=85",
    },
    {
        title: "Technical",
        Icon: Briefcase,
        text: "CNC operators, maintenance technicians, quality inspectors, engineering support — skilled trades placed fast.",
        img: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHx0ZWNobmljYWwlMjBlbmdpbmVlcmluZyUyMHRlY2huaWNpYW58ZW58MHx8fHwxNzc5MTI5OTk3fDA&ixlib=rb-4.1.0&q=85",
    },
    {
        title: "Clerical",
        Icon: FileText,
        text: "Administrative, customer service, accounting, HR — keep your office running with vetted professionals.",
        img: "https://images.unsplash.com/photo-1590650153855-d9e808231d41?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxjbGVyaWNhbCUyMG9mZmljZSUyMHByb2Zlc3Npb25hbCUyMHdvcmtlcnxlbnwwfHx8fDE3NzkxMjk5OTd8MA&ixlib=rb-4.1.0&q=85",
    },
    {
        title: "Construction",
        Icon: HardHat,
        text: "Carpenters, framers, drywall, masonry, heavy equipment operators — boots-on-the-ground crews for commercial and residential job sites.",
        img: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w0NjA1ODR8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjB3b3JrZXJzJTIwc2l0ZXxlbnwwfHx8fDE3NzkxMjk5OTd8MA&ixlib=rb-4.1.0&q=85",
    },
];

export default function Home() {
    const [jobs, setJobs] = useState([]);
    useEffect(() => {
        api.get("/jobs").then((r) => setJobs(r.data.slice(0, 6))).catch(() => {});
    }, []);

    return (
        <PageShell>
            {/* HERO */}
            <section className="relative bg-brand-navy text-white overflow-hidden" data-testid="home-hero">
                <div
                    className="absolute inset-0 bg-cover bg-center opacity-30"
                    style={{ backgroundImage: `url(${HERO_BG})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-brand-navy via-brand-navy/90 to-brand-navy/40" />
                <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-36">
                    <div className="max-w-3xl animate-fade-up">
                        <div className="inline-flex items-center gap-3 mb-6 px-3 py-1.5 border-2 border-brand-orange text-brand-orange text-xs font-heading uppercase tracking-[0.2em] font-bold">
                            <span className="w-2 h-2 bg-brand-orange" />Fort Wayne · Since 1953
                        </div>
                        <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black uppercase leading-[0.95] tracking-tight">
                            Let us match <span className="text-brand-orange">YOU</span><br />with your job.
                        </h1>
                        <p className="mt-8 text-lg md:text-xl text-slate-200 max-w-2xl leading-relaxed">
                            Tower Staffing has been Fort Wayne's full–service employment agency for over 70 years. Industrial, technical, clerical, construction — one application, one call, one of our specialists in touch within 1–2 business days.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link
                                to="/apply"
                                className="bg-brand-orange hover:bg-white hover:text-brand-navy text-white font-heading uppercase font-bold tracking-wider px-8 py-4 rounded-sm transition-colors inline-flex items-center gap-2"
                                data-testid="hero-apply-now-btn"
                            >
                                Apply Now <ArrowRight className="h-5 w-5" />
                            </Link>
                            <Link
                                to="/jobs"
                                className="border-2 border-white/40 hover:border-brand-orange hover:text-brand-orange text-white font-heading uppercase font-bold tracking-wider px-8 py-4 rounded-sm transition-colors"
                                data-testid="hero-view-jobs-btn"
                            >
                                Browse Open Jobs
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats strip */}
            <section className="bg-white border-b-2 border-slate-100">
                <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 divide-x-2 divide-slate-100">
                    {[
                        { k: "70+", v: "Years in Business" },
                        { k: "1–2", v: "Days to First Contact" },
                        { k: "4", v: "Sectors Served" },
                        { k: "1", v: "Streamlined Application" },
                    ].map((s) => (
                        <div key={s.v} className="px-4 py-8 text-center">
                            <div className="font-heading text-4xl md:text-5xl font-black text-brand-navy">{s.k}</div>
                            <div className="mt-2 text-xs uppercase tracking-widest text-slate-500 font-bold">{s.v}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Services */}
            <section className="max-w-7xl mx-auto px-6 md:px-12 py-20" data-testid="home-services">
                <div className="max-w-3xl mb-12">
                    <div className="text-xs uppercase tracking-[0.3em] text-brand-orange font-bold mb-4">Who we place</div>
                    <h2 className="font-heading text-4xl md:text-5xl font-black uppercase text-brand-navy">Four sectors. One agency.</h2>
                    <p className="mt-4 text-slate-600 text-lg">
                        Whether you swing a hammer, run a CNC, drive a forklift, or run the front office — we have the connections to put your skills to work.
                    </p>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {SERVICES.map(({ title, Icon, text, img }) => (
                        <div key={title} className="group border-2 border-slate-200 hover:border-brand-orange bg-white transition-colors" data-testid={`service-card-${title.toLowerCase()}`}>
                            <div className="h-48 bg-slate-100 overflow-hidden">
                                <img src={img} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                            </div>
                            <div className="p-6">
                                <Icon className="h-8 w-8 text-brand-orange mb-3" />
                                <h3 className="font-heading text-2xl font-black uppercase text-brand-navy">{title}</h3>
                                <p className="mt-3 text-slate-600 leading-relaxed">{text}</p>
                                <Link to={`/jobs?category=${title}`} className="mt-4 inline-flex items-center gap-2 font-heading uppercase text-sm font-bold tracking-wider text-brand-navy hover:text-brand-orange">
                                    View {title} Jobs <ArrowRight className="h-4 w-4" />
                                </Link>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Trust */}
            <section className="bg-slate-50 py-20" data-testid="home-trust">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-12 items-center">
                    <div>
                        <div className="text-xs uppercase tracking-[0.3em] text-brand-orange font-bold mb-4">Why Tower</div>
                        <h2 className="font-heading text-4xl md:text-5xl font-black uppercase text-brand-navy">Built on a strong code of ethics.</h2>
                        <p className="mt-4 text-slate-600 text-lg leading-relaxed">
                            Every employee goes through a screening process — application, aptitude, and interview — so our clients get reliable workers and our applicants get the right placement. Family-owned, ethics-driven, and rooted in Fort Wayne for over seventy years.
                        </p>
                        <ul className="mt-6 space-y-3">
                            {[
                                { Icon: ShieldCheck, text: "Strict no-call/no-show policy keeps placements stable" },
                                { Icon: Users, text: "Personal, service-oriented attention from real recruiters" },
                                { Icon: Clock, text: "Fast turnaround — most applicants contacted in 1–2 business days" },
                            ].map((b, i) => (
                                <li key={i} className="flex gap-3 items-start">
                                    <b.Icon className="h-5 w-5 text-brand-orange mt-1 shrink-0" />
                                    <span className="text-slate-700">{b.text}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <img src={SERVICES[0].img} alt="Industrial" className="aspect-[3/4] object-cover w-full" />
                        <img src={SERVICES[1].img} alt="Technical" className="aspect-[3/4] object-cover w-full mt-8" />
                    </div>
                </div>
            </section>

            {/* Featured Jobs */}
            <section className="max-w-7xl mx-auto px-6 md:px-12 py-20" data-testid="home-featured-jobs">
                <div className="flex flex-wrap items-end justify-between mb-10 gap-4">
                    <div>
                        <div className="text-xs uppercase tracking-[0.3em] text-brand-orange font-bold mb-3">Now Hiring</div>
                        <h2 className="font-heading text-4xl md:text-5xl font-black uppercase text-brand-navy">Featured openings</h2>
                    </div>
                    <Link to="/jobs" className="font-heading uppercase tracking-wider font-bold text-sm text-brand-navy hover:text-brand-orange inline-flex items-center gap-2" data-testid="view-all-jobs-link">
                        View All Jobs <ArrowRight className="h-4 w-4" />
                    </Link>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {jobs.map((j) => (
                        <Link key={j.id} to={`/jobs/${j.id}`} className="border-2 border-slate-200 hover:border-brand-orange p-6 bg-white block transition-colors" data-testid={`job-card-${j.id}`}>
                            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-brand-orange">{j.category}</div>
                            <h3 className="font-heading text-xl font-black uppercase text-brand-navy mt-2">{j.title}</h3>
                            <div className="mt-3 text-sm text-slate-600 space-y-1">
                                <div>{j.location} · {j.shift}</div>
                                <div className="font-bold text-brand-navy">{j.pay_rate}</div>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* Final CTA */}
            <section className="bg-brand-navy text-white" data-testid="home-final-cta">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 text-center">
                    <h2 className="font-heading text-4xl md:text-6xl font-black uppercase">Ready to get to work?</h2>
                    <p className="mt-4 text-slate-300 text-lg max-w-2xl mx-auto">
                        One streamlined application — six quick steps — and a recruiter will reach out within 1–2 business days.
                    </p>
                    <Link
                        to="/apply"
                        className="mt-8 inline-flex items-center gap-2 bg-brand-orange hover:bg-white hover:text-brand-navy text-white font-heading uppercase font-bold tracking-wider px-10 py-5 rounded-sm transition-colors text-lg"
                        data-testid="final-cta-apply-btn"
                    >
                        Apply Now <ArrowRight className="h-5 w-5" />
                    </Link>
                </div>
            </section>
        </PageShell>
    );
}
